import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../../redux/Actions/ApiAction';
import AppTable from '../../../components/AppTable/AppTable';
import { setPagination } from '../../../redux/Actions/TableAction';
import { searchStock, searchStockTransaction } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';
import toast from 'react-hot-toast';
import TableSearch from '../../../components/TableSearch/TableSearch';
import TransferStock from './TransferStock';
import StockTransferHistory from './StockTransferHistory';
import StockReceiving from './StockReceiving';

function Warehouse() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    // Local state for loading and error handling
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Search state variables
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    
    // Modal state
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showReceivingModal, setShowReceivingModal] = useState(false);
    const [transferCount, setTransferCount] = useState(0);
    const [receivingCount, setReceivingCount] = useState(0);

    // Permission checks - removed for now
    const canReadWarehouse = true;
    const canManageWarehouse = true;

    // Initialize with empty array to prevent map errors
    useEffect(() => {
        if (!Array.isArray(ApiReducer.apiJson)) {
            dispatch(setApiJson([]));
        }
    }, []);

    // Memoized function to search stock in warehouse
    const searchStockInWarehouse = useCallback(async (searchKey, searchValue) => {
        setIsLoading(true);
        setIsSearching(true);
        setError(null);

        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                stockLocation: 'warehouse' // Filter for warehouse stock only
            };

            // Only add search key/value if both are provided
            if (searchKey && searchValue) {
                // Handle product-related searches with population
                const productFields = ['productName', 'brand', 'productType'];
                const stockFields = ['stockIn', 'stockOut', 'stockAlert', 'type', 'place', 'stockLocation'];
                
                if (productFields.includes(searchKey)) {
                    // For product fields, search in populated productId field
                    searchObj[`productId.${searchKey}`] = {
                        $regex: searchValue,
                        $options: 'i'
                    };
                } else if (stockFields.includes(searchKey)) {
                    // Handle numeric stock fields
                    const numericFields = ['stockIn', 'stockOut', 'stockAlert'];
                    if (numericFields.includes(searchKey)) {
                        searchObj[searchKey] = parseFloat(searchValue) || 0;
                    } else {
                        // For text fields like type, place
                        searchObj[searchKey] = {
                            $regex: searchValue,
                            $options: 'i'
                        };
                    }
                }
            }

            const json = {
                page: TableDataReducer.pagination.page,
                limit: TableDataReducer.pagination.limit,
                search: searchObj,
                populate: ['productId'] // Ensure product data is populated
            };

            const res = await HitApi(json, searchStock);

            // Handle response
            let stockData = [];
            let totalCount = 0;

            if (res?.statusCode === 200) {
                // Stock API response structure
                stockData = res?.data?.docs || res?.stocks || res?.result || [];
                totalCount = res?.data?.totalDocs || res?.total || 0;

                // Ensure we have an array
                if (!Array.isArray(stockData)) {
                    console.warn("Stock API returned non-array data:", stockData);
                    stockData = [];
                }

                // Transform stock data to warehouse format for display
                const warehouseData = stockData.map(stock => ({
                    _id: stock._id,
                    productId: stock.productId?._id || stock.productId,
                    productName: stock.productId?.productName || 'Unknown Product',
                    brand: stock.productId?.brand || '',
                    productType: stock.productId?.productType || '',
                    productImageUrl: stock.productId?.productImageUrl || '',
                    // Stock quantities
                    qty: stock.qty || 0,
                    stockIn: stock.stockIn || 0,
                    stockOut: stock.stockOut || 0,
                    availableQuantity: stock.qty || 0, // Use qty as available quantity
                    stockAlert: stock.stockAlert || 0,
                    // Pricing
                    costPrice: stock.productId?.costPrice || 0,
                    sellPrice: stock.productId?.sellPrice || 0,
                    mrp: stock.productId?.mrp || 0,
                    costPriceDis: stock.productId?.costPriceDis || 0,
                    sellPriceDis: stock.productId?.sellPriceDis || 0,
                    // Product details
                    vendor: stock.productId?.vendor || stock.type || '',
                    type: stock.type || 'professional',
                    place: stock.place || 'warehouse',
                    stockLocation: stock.stockLocation || 'warehouse',
                    unit: stock.productId?.unit || '',
                    netQuantity: stock.productId?.netQuantity || 0,
                    barcodes: stock.productId?.barcodes || [],
                    description: stock.productId?.description || '',
                    createdAt: stock.createdAt,
                    updatedAt: stock.updatedAt
                }));

                dispatch(setApiJson(warehouseData));

                // Update pagination data in Redux
                const paginationData = {
                    total: totalCount,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
            } else {
                const errorMessage = res?.message || res?.error || 'Failed to fetch warehouse stock';
                console.error("Stock API Error:", errorMessage);
                setError(errorMessage);
                dispatch(setApiJson([]));
                toast.error('Failed to fetch warehouse stock. Please try again.');
            }
        } catch (error) {
            console.error("Error fetching warehouse stock:", error);
            setError('Failed to fetch warehouse stock. Please check your connection and try again.');
            dispatch(setApiJson([]));
            toast.error('Error fetching warehouse stock. Please try again.');
        } finally {
            setIsLoading(false);
            setIsSearching(false);
        }
    }, [dispatch, TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

    // Function to fetch transfer count (items transferred FROM warehouse)
    const fetchTransferCount = useCallback(async () => {
        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                status: 'transferd',
                transferFrom: 'warehouse' // Only transfers from warehouse
            };

            const json = {
                page: 1,
                limit: 1, // We only need the count, not the actual data
                search: searchObj
            };

            const res = await HitApi(json, searchStockTransaction);

            if (res?.statusCode === 200) {
                const totalCount = res?.data?.totalDocs || res?.total || 0;
                setTransferCount(totalCount);
            }
        } catch (error) {
            console.error("Error fetching transfer count:", error);
            // Don't show error toast for count fetch failures
        }
    }, []);

    // Function to fetch receiving count (items transferred TO warehouse FROM salon)
    const fetchReceivingCount = useCallback(async () => {
        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                status: 'transferd',
                transferFrom: 'salon' // Items coming from salon to warehouse
            };

            const json = {
                page: 1,
                limit: 1, // We only need the count, not the actual data
                search: searchObj
            };

            const res = await HitApi(json, searchStockTransaction);

            if (res?.statusCode === 200) {
                const totalCount = res?.data?.totalDocs || res?.total || 0;
                setReceivingCount(totalCount);
            }
        } catch (error) {
            console.error("Error fetching receiving count:", error);
            // Don't show error toast for count fetch failures
        }
    }, []);

    useEffect(() => {
        if (canReadWarehouse) {
            searchStockInWarehouse();
            fetchTransferCount(); // Fetch transfer count on load
            fetchReceivingCount(); // Fetch receiving count on load
        }
    }, [searchStockInWarehouse, canReadWarehouse, fetchTransferCount, fetchReceivingCount]);

    // Search functionality
    const handleSearch = () => {
        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }

        let searchValue = valueSearched.trim();

        // Convert numeric fields to numbers for stock fields
        const numericFields = ['stockIn', 'stockOut', 'stockAlert', 'quantity'];
        if (numericFields.includes(optionSelect)) {
            const numericValue = parseFloat(searchValue);
            if (isNaN(numericValue)) {
                toast.error('Please enter a valid number for numeric fields');
                return;
            }
            searchValue = numericValue;
        }

        searchStockInWarehouse(optionSelect, searchValue);
    };

    // Search options for warehouse stock
    const options = [
        { label: 'Product Name', value: 'productName' },
        { label: 'Brand', value: 'brand' },
        { label: 'Product Type', value: 'productType' },
        { label: 'Stock In', value: 'stockIn' },
        { label: 'Stock Out', value: 'stockOut' },
        { label: 'Stock Alert Level', value: 'stockAlert' },
        { label: 'Usage Type (Professional/Retail)', value: 'type' },
        { label: 'Storage Place', value: 'place' },
        { label: 'Stock Location (Warehouse/Salon)', value: 'stockLocation' },
    ];

    // Format currency with proper validation
    const formatCurrency = useCallback((amount) => {
        if (amount === null || amount === undefined || amount === '' || isNaN(amount)) {
            return '₹0.00';
        }
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        if (isNaN(numAmount)) return '₹0.00';
        return `₹${numAmount.toFixed(2)}`;
    }, []);

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    // Define warehouse table headers
    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "start",
            priority: 1,
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Product",
            key: "productName",
            priority: 1,
            render: (value, row) => (
                <div className="flex items-start">
                    {row.productImageUrl ? (
                        <img
                            src={row.productImageUrl}
                            alt={value || 'Product'}
                            className="w-10 h-10 rounded-lg mr-3 object-cover flex-shrink-0"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0"
                        style={{ display: row.productImageUrl ? 'none' : 'flex' }}>
                        <span className="text-lg">📦</span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate" title={value}>
                            {value || 'Unnamed Product'}
                        </div>
                        {row.brand && (
                            <div className="text-sm text-gray-500 truncate mt-1" title={row.brand}>
                                {row.brand}
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: "Stock Details",
            key: "stockDetails",
            align: "center",
            priority: 1,
            render: (_, row) => (
                <div className="text-center">
                    <div className={`font-medium px-2 py-1 rounded text-sm mb-1 ${
                        row.availableQuantity <= 0 ? 'bg-red-100 text-red-800' : 
                        row.availableQuantity < 10 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-green-100 text-green-800'
                    }`}>
                        Available: {row.availableQuantity}
                    </div>
                    <div className="text-xs text-gray-600">
                        <div>Qty: {row.qty || 0}</div>
                        <div>In: {row.stockIn} | Out: {row.stockOut}</div>
                        {row.stockAlert > 0 && (
                            <div className="text-orange-600">Alert: {row.stockAlert}</div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: "Pricing Details",
            key: "pricingDetails",
            align: "start",
            priority: 2,
            render: (_, row) => (
                <div className="text-sm">
                    <div className="font-medium text-green-600">MRP: {formatCurrency(row.mrp)}</div>
                    <div className="text-gray-600">Cost: {formatCurrency(row.costPrice)}</div>
                    <div className="text-blue-600">Sell: {formatCurrency(row.sellPrice)}</div>
                    {(row.costPriceDis > 0 || row.sellPriceDis > 0) && (
                        <div className="text-xs text-orange-600">
                            {row.costPriceDis > 0 && `Cost Disc: ${row.costPriceDis}%`}
                            {row.sellPriceDis > 0 && ` Sell Disc: ${row.sellPriceDis}%`}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: "Product Info",
            key: "productInfo",
            align: "start", 
            priority: 3,
            render: (_, row) => (
                <div className="text-xs">
                    {row.unit && row.netQuantity > 0 && (
                        <div className="text-blue-600 font-medium">
                            {row.netQuantity} {row.unit}
                        </div>
                    )}
                    {row.barcodes && row.barcodes.length > 0 && (
                        <div className="text-gray-500 truncate" title={row.barcodes[0]}>
                            Barcode: {row.barcodes[0]}
                        </div>
                    )}
                    {row.description && (
                        <div className="text-gray-500 truncate" title={row.description}>
                            {row.description}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: "Type",
            key: "productType",
            priority: 3,
            render: (value) => (
                <span className="capitalize">
                    {value ? value.replace(/[-_]/g, ' ') : 'N/A'}
                </span>
            )
        },
        {
            title: "Usage Type",
            key: "type",
            priority: 3,
            render: (value) => (
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    value === 'professional' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-orange-100 text-orange-800'
                }`}>
                    {value === 'professional' ? 'Professional' : 'Retail'}
                </span>
            )
        },
        {
            title: "Place",
            key: "place",
            priority: 4,
            render: (value) => (
                <span className="capitalize" title={value}>
                    {value || 'Warehouse'}
                </span>
            )
        },
        {
            title: "Location",
            key: "stockLocation",
            priority: 3,
            render: (value) => (
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    value === 'warehouse' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                }`}>
                    {value === 'warehouse' ? 'Warehouse' : 'Salon'}
                </span>
            )
        }
    ];

    const warehouseData = Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : [];

    const handleClearSearch = () => {
        setOptionSelect('');
        setValueSearched('');
        searchStockInWarehouse();
    };

    return (
        <div className="p-5">
            <PageHeader
                title="Warehouse Management"
                description="View and search products in warehouse inventory"
            />

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-medium">Error Loading Warehouse</h4>
                            <p className="mt-1">{error}</p>
                        </div>
                        <button
                            onClick={() => searchStockInWarehouse()}
                            disabled={isLoading}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                            {isLoading ? 'Loading...' : 'Retry'}
                        </button>
                    </div>
                </div>
            )}

            {!canReadWarehouse ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading permissions...</div>
                </div>
            ) : (
                <>
                    {/* Action Buttons */}
                    {canManageWarehouse && (
                        <div className="mb-4 flex space-x-3">
                            <button
                                onClick={() => navigate("/warehouse/add?location=warehouse&type=all")}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                disabled={isLoading}
                            >
                                Add to Warehouse
                            </button>
                            <button
                                onClick={() => setShowTransferModal(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                disabled={isLoading}
                            >
                                Transfer Stock
                            </button>
                            <button
                                onClick={() => setShowHistoryModal(true)}
                                className="relative px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                                disabled={isLoading}
                            >
                                Check Stock Transfer
                                {transferCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                                        {transferCount > 99 ? '99+' : transferCount}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setShowReceivingModal(true)}
                                className="relative px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                                disabled={isLoading}
                            >
                                Stock Receiving
                                {receivingCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                                        {receivingCount > 99 ? '99+' : receivingCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    )}

                    <AppTable
                        TH={tableHeaders}
                        TD={warehouseData}
                        isLoading={isLoading || isSearching}
                        emptyMessage={error ? "Failed to load warehouse data. Please try again." : "No products found in warehouse."}
                        TableSearch={
                            <TableSearch
                                options={options}
                                onClick={handleSearch}
                                optionSelect={optionSelect}
                                setOptionSelect={setOptionSelect}
                                setValueSearched={setValueSearched}
                                valueSearched={valueSearched}
                                onClear={handleClearSearch}
                            />
                        }
                    />
                </>
            )}

            {/* Transfer Stock Modal */}
            {showTransferModal && (
                <TransferStock 
                    isModal={true}
                    onClose={() => setShowTransferModal(false)}
                    onTransferComplete={() => {
                        setShowTransferModal(false);
                        searchStockInWarehouse(); // Refresh the warehouse data
                    }}
                />
            )}

            {/* Stock Transfer History Modal */}
            {showHistoryModal && (
                <StockTransferHistory 
                    isModal={true}
                    onClose={() => setShowHistoryModal(false)}
                    fromLocation="warehouse"
                    onDataChange={() => {
                        fetchTransferCount(); // Refresh transfer count when data changes
                        fetchReceivingCount(); // Refresh receiving count when data changes
                    }}
                />
            )}

            {/* Stock Receiving Modal */}
            {showReceivingModal && (
                <StockReceiving 
                    isModal={true}
                    onClose={() => setShowReceivingModal(false)}
                    transferFrom="salon"
                    productType={null}
                />
            )}
        </div>
    );
}

export default Warehouse;
